import { EditorState, Plugin } from 'hubspot-prosemirror/pm/state';
import { Fragment, Slice } from 'hubspot-prosemirror/pm/model';
import parseHTML from 'rich-text-lib/utils/parseHTML';
import { convertToPlaintextState, removeNonPlaintextNodes } from '../utils/plaintextUtils';
import { linkify } from './link/linkify';

/** Configuration for paste behavior overrides */

/**
 * TODO: move this into state for the PasteOverridePlugin
 */ // eslint-disable-next-line no-restricted-syntax
let pasteOverrideConfig;
/**
 * Create a transformer which removes a given CSS rule from any children
 * that have a style attribute.
 *
 * @param styleProperty CSS property to remove from all DOM nodes
 * @returns {PasteTransformer}
 */
const styleTransformer = styleProperty => {
  return htmlEl => {
    if (htmlEl.querySelectorAll('[data-pm-slice]').length === 0) {
      htmlEl.querySelectorAll('[style]').forEach(el => el.style.removeProperty(styleProperty));
    }
    return htmlEl;
  };
};

/**
 * Create a transformer which removes all instances of an HTML element from
 * the children of a DOM node.
 *
 * @param tag Name of an HTML tag to remove
 * @returns {PasteTransformer}
 */
const removeTagTransformer = tag => {
  return htmlEl => {
    if (htmlEl.querySelectorAll('[data-pm-slice]').length === 0) {
      const collection = htmlEl.getElementsByTagName(tag);
      while (collection.length > 0) {
        collection[0].remove();
      }
    }
    return htmlEl;
  };
};

/**
 * Create a transformer which replaces all instances of an HTML element with
 * their text content as a text node.
 *
 * @param tag Name of an HTML tag to replace with text nodes
 * @returns {PasteTransformer}
 */
const tagTextTransformer = tag => {
  return htmlEl => {
    if (htmlEl.querySelectorAll('[data-pm-slice]').length === 0) {
      const collection = htmlEl.getElementsByTagName(tag);
      while (collection.length > 0) {
        collection[0].replaceWith(document.createTextNode(collection[0].innerText));
      }
    }
    return htmlEl;
  };
};
const pasteTransformers = {
  ['stripTextHighlights']: styleTransformer('background-color'),
  ['stripTextColor']: styleTransformer('color'),
  ['stripTextStyle']: styleTransformer('font-family'),
  ['stripTextSize']: styleTransformer('font-size'),
  ['stripImages']: removeTagTransformer('img'),
  ['stripLinks']: tagTextTransformer('a')
};

/**
 * Build a paste slice from clipboard plain text: one paragraph per line, with both
 * ends open (openStart/openEnd = 1) so a single-line paste merges inline at the cursor
 * rather than starting a new block. Mirrors a text-only paste.
 */
function plainTextToSlice(schema, text) {
  const paragraphs = text.split(/\r\n|\r|\n/).map(line => schema.nodes.paragraph.create(null, line ? schema.text(line) : null));
  return new Slice(Fragment.from(paragraphs), 1, 1);
}
export const PasteOverridePlugin = new Plugin({
  state: {
    init() {
      return {};
    },
    apply(tr, currentPluginState) {
      const meta = tr.getMeta(PasteOverridePlugin);
      if (meta) {
        pasteOverrideConfig = meta.pasteOverrideConfig || pasteOverrideConfig;
        return meta;
      }
      return currentPluginState;
    }
  },
  props: {
    transformPastedHTML: html => {
      if (!pasteOverrideConfig) return html;
      let htmlEl = parseHTML(html);
      for (const prop in pasteOverrideConfig) {
        if (pasteOverrideConfig[prop] && pasteTransformers[prop]) {
          htmlEl = pasteTransformers[prop](htmlEl);
        }
      }
      return htmlEl.outerHTML;
    },
    handlePaste: (view, event, slice) => {
      var _pasteOverrideConfig;
      if ((_pasteOverrideConfig = pasteOverrideConfig) !== null && _pasteOverrideConfig !== void 0 && _pasteOverrideConfig.usePlaintextMode) {
        var _event$clipboardData$, _event$clipboardData, _event$clipboardData$2, _event$clipboardData2, _event$clipboardData$3, _event$clipboardData3;
        // ProseMirror parses a pasted HTML <table> into a slice whose text is duplicated
        // across table/row/cell nesting, because this schema has no table nodes — so a
        // spreadsheet paste (Google Sheets, Excel) lands several times over. For that one
        // case build the paste from the clipboard's own plain-text copy instead. Other
        // rich content still flows through the slice below so the plaintext conversion
        // (link URLs kept as "text (url)", list markers) is preserved.
        //
        // Scope note: this only corrects the table case when a text/plain flavor is
        // present, which every real spreadsheet/browser copy provides. A table pasted as
        // HTML with no text/plain (e.g. a programmatic ClipboardEvent) still falls through
        // to the duplicating slice path — a rarer case left for a follow-up.
        const clipboardHtml = (_event$clipboardData$ = event === null || event === void 0 || (_event$clipboardData = event.clipboardData) === null || _event$clipboardData === void 0 ? void 0 : _event$clipboardData.getData('text/html')) !== null && _event$clipboardData$ !== void 0 ? _event$clipboardData$ : '';
        // Gate on the presence of the text/plain flavor, not its truthiness: a table of
        // empty cells legitimately exposes text/plain as '', and treating that as absent
        // would fall back to the duplicating slice path.
        const hasPlainText = Array.from((_event$clipboardData$2 = event === null || event === void 0 || (_event$clipboardData2 = event.clipboardData) === null || _event$clipboardData2 === void 0 ? void 0 : _event$clipboardData2.types) !== null && _event$clipboardData$2 !== void 0 ? _event$clipboardData$2 : []).includes('text/plain');
        const plainText = (_event$clipboardData$3 = event === null || event === void 0 || (_event$clipboardData3 = event.clipboardData) === null || _event$clipboardData3 === void 0 ? void 0 : _event$clipboardData3.getData('text/plain')) !== null && _event$clipboardData$3 !== void 0 ? _event$clipboardData$3 : '';
        const sourceSlice = hasPlainText && /<table[\s/>]/i.test(clipboardHtml) ? plainTextToSlice(view.state.schema,
        // Spreadsheet copies carry a trailing newline; drop one so the paste
        // does not gain a trailing empty paragraph.
        plainText.replace(/(?:\r\n|\r|\n)$/, '')) : slice;
        let sliceState = EditorState.create({
          schema: view.state.schema
        });
        sliceState = sliceState.apply(sliceState.tr.replace(0, sliceState.doc.content.size, sourceSlice));
        const doc = removeNonPlaintextNodes(convertToPlaintextState(sliceState, pasteOverrideConfig.plaintextOptions, true)).doc;
        const plainSlice = doc.slice(1, doc.content.size - 1);
        const finalSlice = pasteOverrideConfig.linkifyOptions ? new Slice(linkify(plainSlice.content, pasteOverrideConfig.linkifyOptions), plainSlice.openStart, plainSlice.openEnd) : plainSlice;
        view.dispatch(view.state.tr.replaceSelection(finalSlice));
        return true;
      }
      return false;
    }
  }
});